import { InfoDot } from '../hiring/HiringForm';
import { DecorationPicker } from './DecorationPicker';
import { RecentPushes } from './RecentPushes';
import type { CelebrationType, MissedSubtype, TestPushType } from './bannerOptions';
import { ChoiceCard, FIELD_INPUT, FieldLabel, PillRow } from './bannerFormBits';
import type { PushLogEntry } from './pushLog';
import type { TestPushes } from './useTestPushes';
import { getLocalYesterdayDateString as yesterdayStr } from '../../utils/dateHelpers';

const TYPES: { value: TestPushType; label: string; line: string; title: string }[] = [
  {
    value: 'random_attendance',
    label: 'Random Attendance',
    line: 'Real slot, 15 min',
    title: 'Writes a real 15-minute slot for that person today, then pushes.',
  },
  {
    value: 'mood_check',
    label: 'Mood Check',
    line: 'Push only',
    title: 'Sends the Mood Check push. Nothing is saved.',
  },
  {
    value: 'celebration',
    label: 'Celebration',
    line: 'Shows for 1 hour',
    title: 'Birthday, anniversary or festival. Shows for 1 hour.',
  },
  {
    value: 'missed_attendance',
    label: 'Missed Attendance',
    line: 'Push only',
    title: 'Sends the real missed attendance copy. Nothing is saved.',
  },
];

const CELEBRATIONS: { value: CelebrationType; label: string }[] = [
  { value: 'birthday', label: '🎂 Birthday' },
  { value: 'anniversary', label: '🎉 Anniversary' },
  { value: 'festival', label: '🪔 Festival' },
];

const SCENARIOS: { value: MissedSubtype; label: string }[] = [
  { value: 'no-attendance', label: "Didn't mark attendance" },
  { value: 'missing-signout', label: 'Forgot to sign out' },
];

// Mirrors the copy in apps/api/.../missed-attendance-scheduler.service.ts
// (buildMissedAttendanceMessage) for a live form preview. The send response
// returns the authoritative server-built message, shown in the log.
const missedPreview = (subtype: MissedSubtype, date: string) =>
  subtype === 'missing-signout'
    ? {
        title: 'You forgot to sign out yesterday',
        body: `Your sign-out for ${date} is missing. Please regularize your attendance.`,
      }
    : {
        title: 'You missed marking attendance yesterday',
        body: `We don't have your attendance for ${date}. Please regularize it or apply for leave.`,
      };

/** Fire one test at one device by employee code, and see what the server said beside it. */
export function TestPushesTab({ test, log }: { test: TestPushes; log: PushLogEntry[] }) {
  const {
    empCode,
    setEmpCode,
    pushType,
    setPushType,
    slot,
    setSlot,
    selfieRequired,
    setSelfieRequired,
    celebrationType,
    setCelebrationType,
    missedSubtype,
    setMissedSubtype,
    anniversaryYears,
    setAnniversaryYears,
    festivalName,
    changeFestivalName,
    festivalDecoration,
    pickFestivalDecoration,
  } = test;
  const missed = missedPreview(missedSubtype, yesterdayStr());

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,42rem)_minmax(0,1fr)]">
      <section className="flex flex-col gap-5 rounded-lg border border-gray-200 bg-white p-4 sm:p-6">
        <div>
          <FieldLabel text="Notification Type" />
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {TYPES.map((t) => (
              <ChoiceCard
                key={t.value}
                label={t.label}
                line={t.line}
                title={t.title}
                on={pushType === t.value}
                onClick={() => setPushType(t.value)}
              />
            ))}
          </div>
        </div>

        <div>
          <FieldLabel text="Employee Code" htmlFor="test-push-code" />
          <input
            id="test-push-code"
            type="text"
            value={empCode}
            onChange={(e) => setEmpCode(e.target.value)}
            placeholder="e.g. 322485"
            className={FIELD_INPUT}
          />
        </div>

        {pushType === 'random_attendance' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <FieldLabel text="Slot Number" />
              <PillRow
                options={[
                  { value: 1, label: 'Slot 1' },
                  { value: 2, label: 'Slot 2' },
                ]}
                value={slot}
                onChange={setSlot}
              />
            </div>
            <div>
              <FieldLabel
                text="Capture mode"
                hint={`With selfie: the employee must capture a selfie to mark the slot. Confirm only: the employee only confirms "I'm present at the store", no camera. GPS is still captured and geofence-checked either way. On a real slot this is rolled per employee from the project's selfie percentage. Here it's forced, so you can test both branches on demand without changing the project setting.`}
              />
              <PillRow
                options={[
                  { value: 'selfie', label: 'With selfie' },
                  { value: 'confirm', label: 'Confirm only' },
                ]}
                value={selfieRequired ? 'selfie' : 'confirm'}
                onChange={(v) => setSelfieRequired(v === 'selfie')}
              />
            </div>
          </div>
        )}

        {pushType === 'celebration' && (
          <>
            <div>
              <FieldLabel
                text="Celebration"
                hint="Sending fires the push and activates a 1-hour homepage card override for this employee only. Real employee DOB/DOJ and the daily fan-out are untouched. Override auto-expires."
              />
              <PillRow options={CELEBRATIONS} value={celebrationType} onChange={setCelebrationType} />
            </div>

            {celebrationType === 'anniversary' && (
              <div>
                <FieldLabel text="Years Completed" htmlFor="test-push-years" />
                <input
                  id="test-push-years"
                  type="number"
                  min={1}
                  max={50}
                  value={anniversaryYears}
                  onChange={(e) => setAnniversaryYears(Math.max(1, Number(e.target.value) || 1))}
                  className="w-32 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            )}

            {celebrationType === 'festival' && (
              <>
                <div>
                  <FieldLabel text="Festival Name" htmlFor="test-push-festival" />
                  <input
                    id="test-push-festival"
                    type="text"
                    value={festivalName}
                    onChange={(e) => changeFestivalName(e.target.value)}
                    placeholder="e.g. Diwali, Holi, Eid"
                    className={FIELD_INPUT}
                  />
                </div>
                <div>
                  <FieldLabel
                    text="Decoration"
                    hint="The animation that plays on the homepage card and the full-screen wish. Auto-matched to the festival name — change it if you'd rather."
                  />
                  <DecorationPicker value={festivalDecoration} onChange={pickFestivalDecoration} />
                </div>
              </>
            )}

            {celebrationType !== 'festival' && (
              <p className="flex items-center gap-1 text-[13px] text-gray-500">
                {celebrationType === 'birthday'
                  ? '🎈 Balloons, fixed for birthdays.'
                  : '🎊 Confetti, fixed for work anniversaries.'}
                <InfoDot text="Only festivals let you choose, so the two milestones stay instantly recognisable." />
              </p>
            )}
          </>
        )}

        {pushType === 'missed_attendance' && (
          <>
            <div>
              <FieldLabel
                text="Scenario"
                hint="Sends the real 7 AM reminder push to this employee's device immediately. This is only a delivery test — it does not check their actual attendance or write to the sent-log, so the daily fan-out is unaffected."
              />
              <PillRow options={SCENARIOS} value={missedSubtype} onChange={setMissedSubtype} />
            </div>

            {/* Live preview of the exact message employees receive. */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Message preview</p>
              <p className="text-sm font-semibold text-gray-900">{missed.title}</p>
              <p className="mt-0.5 text-sm text-gray-600">{missed.body}</p>
            </div>
          </>
        )}
      </section>

      <RecentPushes log={log} />
    </div>
  );
}
